/**
 * @prettier
 */

import _every from 'lodash/every';
import moment from 'moment';

export function extractWeddingNames(weddingDetails) {
  if (
    !(weddingDetails.fullName && weddingDetails.fullName.split(' ').length > 1) ||
    (!weddingDetails.hasNoPartner &&
      !(weddingDetails.partnerFullName && weddingDetails.partnerFullName.split(' ').length > 1))
  ) {
    return null;
  }

  const [ownerUserFirstName, ...ownerUserLastNames] = weddingDetails.fullName.split(' ');
  const [partnerUserFirstName, ...partnerUserLastNames] = weddingDetails.partnerFullName
    ? weddingDetails.partnerFullName.split(' ')
    : ['Someone', 'Special'];

  return {
    owner_first_name: ownerUserFirstName,
    owner_last_name: ownerUserLastNames.join(' '),
    partner_first_name: partnerUserFirstName,
    partner_last_name: partnerUserLastNames.join(' '),
  };
}

export function extractUserWeddingDetails(userContext) {
  return (
    userContext &&
    userContext.user_role_account_weddings &&
    userContext.user_role_account_weddings[0]
  );
}

export function findVariation(uuid, cardSuiteDetails, isMagnet = false) {
  const mediumInfo = isMagnet ? cardSuiteDetails.mediums.magnet : cardSuiteDetails.mediums.paper;

  const variation = mediumInfo.variations.find(v => v.uuid === uuid);

  return variation;
}

export function lookupSelectedVariation(selected, cardSuiteDetails, isMagnet = false) {
  const mediumInfo = isMagnet ? cardSuiteDetails.mediums.magnet : cardSuiteDetails.mediums.paper;

  const variation = mediumInfo.variations.find(v => {
    return _every(
      Object.keys(selected).map(k => selected[k] && v.option_values[k] === selected[k])
    );
  });

  return variation;
}

export function lookupDefaultVariation(cardSuiteDetails, isMagnet = false) {
  if (!cardSuiteDetails) {
    return null;
  }

  let defaultOptions =
    cardSuiteDetails.mediums[isMagnet ? 'magnet' : 'paper'].default_variation_options;

  defaultOptions = {
    ...defaultOptions,
    size: isMagnet ? 'magnet' : defaultOptions.size,
  };

  const defaultVariation = lookupSelectedVariation(defaultOptions, cardSuiteDetails, isMagnet);

  return defaultVariation;
}

export function getVariationOptions(cardSuiteDetails, isMagnet = false) {
  if (!cardSuiteDetails) {
    return null;
  }

  const mediumInfo = isMagnet ? cardSuiteDetails.mediums.magnet : cardSuiteDetails.mediums.paper;

  if (!mediumInfo) {
    return null;
  }

  return { ...mediumInfo.available_options };
}

export function matchColorToCardVariation(cardSuiteDetails, color, isMagnet = false) {
  const mediumInfo = isMagnet ? cardSuiteDetails.mediums.magnet : cardSuiteDetails.mediums.paper;
  const defaultOptions = mediumInfo.default_variation_options;

  const match = {
    ...defaultOptions,
    color: color || defaultOptions.color,
    size: isMagnet ? 'magnet' : defaultOptions.size,
  };

  const variation = lookupSelectedVariation(match, cardSuiteDetails, isMagnet);

  return variation;
}

export function parseWeddingDate(date) {
  if (!date) {
    return null;
  }

  if (date.length !== 10) {
    return null;
  }

  const parsedDate = moment(
    `${date.substring(0, 4)}-${date.substring(5, 7)}-${date.substring(8, 10)}`
  );

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate;
}

export function createSubstitutions(weddingDetails) {
  const substitutions = {};

  if (weddingDetails) {
    const names = extractWeddingNames(weddingDetails);

    const { date } = weddingDetails;

    const parsedDate = parseWeddingDate(date);

    if (names) {
      if (names.owner_first_name.length > 0) {
        substitutions.PrimaryFirstName = names.owner_first_name;
        substitutions.PrimaryFirstNameInitial = names.owner_first_name[0].toUpperCase();
      }

      if (names.owner_last_name.length > 0) {
        substitutions.PrimaryLastName = names.owner_last_name;
        substitutions.PrimaryLastNameInitial = names.owner_last_name[0].toUpperCase();
      }

      if (names.partner_first_name.length > 0) {
        substitutions.PartnerFirstName = names.partner_first_name;
        substitutions.PartnerFirstNameInitial = names.partner_first_name[0].toUpperCase();
      }

      if (names.partner_last_name.length > 0) {
        substitutions.PartnerLastName = names.partner_last_name;
        substitutions.PartnerLastNameInitial = names.partner_last_name[0].toUpperCase();
      }
    }

    if (parsedDate) {
      substitutions.Month = parsedDate.format('MMMM');
      substitutions.MonthNumber = parsedDate.format('M');
      substitutions.MonthNumber2Digits = parsedDate.format('MM');
      substitutions.Day = parsedDate.format('D');
      substitutions.DayNumber = parsedDate.format('D');
      substitutions.DayNumber2Digits = parsedDate.format('DD');
      substitutions.Year = parsedDate.format('YYYY');
      substitutions.Year2Digits = parsedDate.format('YY');
    }
  }

  return substitutions;
}

export function templateHasCustomImage(template, page = 0) {
  if (!template) {
    return false;
  }

  if (!template.pages[page]) {
    return false;
  }

  return !!template.pages[page].elements.find(e => e.content_type === 'IMAGE' && e.customizable);
}

export function normalizeDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return null;

  const fmt = dateString.split('-').join('');

  return `${fmt.substring(0, 4)} ${fmt.substring(4, 6)} ${fmt.substring(6, 8)}`
    .split(' ')
    .filter(s => s.length > 0)
    .join('-');
}

const DATE_REGEX = /\d{4}-\d{2}-\d{2}/;

export function dateIsComplete(dateString) {
  return !dateString || DATE_REGEX.test(dateString);
}

export function getValidatedDate(dateString) {
  let validatedDate = null;

  if (dateIsComplete(dateString)) {
    let parsedDate = parseWeddingDate(dateString);

    if (parsedDate) {
      // If date has passed, select today
      parsedDate = moment.max(parsedDate, moment());
      validatedDate = parsedDate.format('YYYY-MM-DD');
    }
  }

  return validatedDate;
}

export function getColors(cardSuiteDetails) {
  const colors =
    cardSuiteDetails.mediums &&
    cardSuiteDetails.mediums.paper &&
    cardSuiteDetails.mediums.paper.available_options &&
    cardSuiteDetails.mediums.paper.available_options.color &&
    cardSuiteDetails.mediums.paper.available_options.color.options;

  return colors || [];
}

export function getIsSampleCustomizable(cardSuiteDetails, isMagnet = false) {
  if (!cardSuiteDetails) {
    return false;
  }

  return !(cardSuiteDetails.letterpress || cardSuiteDetails.foil || isMagnet);
}



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/util/helpers.js